import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { startTransition, useActionState, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RegisterFormSchema from '@/components/RegisterPage/schama';
import type { RegisterFormData } from '@/stores/register/types';
import { checkUserExistence, register, registerWithSocialAuth, type RegisterActionState, type RegisterUserPayload, type SocialRegisterActionState, type SocialRegisterUserPayload } from '@/app/(auth)/actions';
import { toast } from '@/components/toast';
import useSecureAnalytics from '@/hooks/useRudderAnalytics';
import { useRouter, useSearchParams } from 'next/navigation';
import { featureFlags } from '@/config/config';
import useRegisterStore from '@/stores/register';
import { getNativePlatform, getPlatformOS } from '@/lib/utils/platform';
import { useFirstTouchUTM } from '@/hooks/use-first-touch-utm';
import { useIfpCampaignParams } from '@/hooks/use-ifp-campaign-params';
import { mergeTrackingParams } from '@/lib/utm-tracker';
import useIsWebview from '@/hooks/use-is-webview';
import { getSafeRedirectUrl } from '@/lib/utils';
import { getHomePage } from '@/hooks/use-home-page';
import { sendNativeAuthToMobile, isInMobileWebView } from '@/utils/sendNativeAuth';
import { signIn as nextAuthSignIn } from 'next-auth/react';
import { generateFormDataToSend, getRegisterCheckErrorMessage, registerFormSubmitResultDataError, resolveRegisterCompletion, type RegisterPageFactory, type RegisterStatusCallBackStatusType, type SubscriptionType } from '@/components/RegisterPage/factory-helpers';
import { registrationErrorCodeFromStatus } from '@/lib/analytics/registration-error-codes';
import { pushRegisterSuccessDataLayer } from '@/lib/analytics/gtm-datalayer';
const useRegisterPageFactory = (): RegisterPageFactory => {
  const [isWebView] = useIsWebview();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect_type = searchParams.get('redirect_type');
  const subscription_plan = searchParams.get('subscription_plan') as SubscriptionType;
  const callbackUrl = searchParams.get('callbackUrl');

  // Social auth params
  const providerParam = searchParams.get('provider');
  const providerUserIdParam = searchParams.get('providerUserId');
  const skipPassword = searchParams.get('skipPassword') === 'true';
  const isSocialAuth = !!(skipPassword && providerParam && providerUserIdParam);
  // Signed proof minted by the OAuth signIn callback (issue #5801). Required
  // by registerWithSocialAuth — carried through untouched from the redirect
  // URL to the Server Action call below.
  const socialRegistrationTokenParam = searchParams.get('socialRegistrationToken');
  const analytics = useSecureAnalytics();
  const firstTouchUTM = useFirstTouchUTM();
  const ifpCampaignParams = useIfpCampaignParams();
  const hasTrackedPageView = useRef(false);

  // Local loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get store state and actions
  const {
    formData,
    setFormData,
    setPhoneNumber,
    setPendingFormData,
    setUserId,
    setRegistrationToken,
    nextStep,
    verificationTokenId
  } = useRegisterStore(['formData', 'setFormData', 'setPhoneNumber', 'setPendingFormData', 'setUserId', 'setRegistrationToken', 'nextStep', 'verificationTokenId']);
  const registerForm = useForm<RegisterFormData>({
    resolver: zodResolver(RegisterFormSchema(isWebView, isSocialAuth)),
    mode: 'onChange',
    defaultValues: {
      email: '',
      phoneNumber: '',
      dateOfBirth: '',
      dateOfBirthTerms: true,
      avatarImage: '',
      document: '',
      name: '',
      password: '',
      passwordConfirmation: ''
    },
    values: formData as RegisterFormData // Sync with store
  });

  // Use different action state based on social auth
  const [state, formAction, isPending] = useActionState<RegisterActionState, RegisterUserPayload>(register, {
    status: 'idle'
  });
  const [socialState, socialFormAction, socialIsPending] = useActionState<SocialRegisterActionState, SocialRegisterUserPayload>(registerWithSocialAuth, {
    status: 'idle'
  });

  // Tracks whether a register/social action is in flight so we can reset
  // `isSubmitting` when it completes — even if `state.status` doesn't change
  // between attempts (e.g. failed → failed). Without this, the button gets
  // stuck in "Criando conta..." forever on repeated identical failures.
  // Tracks which register action is in flight ('credentials' | 'social' | null)
  // so the completion effect below can fire the right callback every time the
  // action finishes — even when state.status doesn't change between attempts
  // (e.g. document_exists -> document_exists), which a status-only effect
  // would silently skip.
  const actionInFlightRef = useRef<'credentials' | 'social' | null>(null);

  // Sync form changes back to store
  useEffect(() => {
    const subscription = registerForm.watch(values => {
      setFormData(values as Partial<RegisterFormData>);
    });
    return () => subscription.unsubscribe();
  }, [registerForm, setFormData]);
  const handleSubmit = useCallback(async (data: RegisterFormData) => {
    setIsSubmitting(true);

    // Generate form data with social auth params if applicable
    const socialAuthParams = isSocialAuth && providerParam && providerUserIdParam ? {
      provider: providerParam,
      providerUserId: providerUserIdParam,
      socialRegistrationToken: socialRegistrationTokenParam ?? undefined
    } : undefined;
    const formData_0 = generateFormDataToSend(data, socialAuthParams);

    // Thread the OTP proof id (captured by the OTP step) into the register
    // action payload so the action atomically consumes it before createUser
    // (issue #5339).
    if (verificationTokenId) {
      formData_0.append('verificationTokenId', verificationTokenId);
    }
    setPhoneNumber(`55${(data?.phoneNumber ?? '').replace(/\D/g, '').trim()}`);
    setPendingFormData(formData_0);
    const checkUserExistenceActionStateResult = await checkUserExistence({
      status: 'idle',
      exists: false,
      byEmail: false,
      byPhone: false,
      byDocument: false
    }, formData_0);
    if (checkUserExistenceActionStateResult.status === 'failed') {
      toast({
        type: 'error',
        description: getRegisterCheckErrorMessage('failed')
      });
      setIsSubmitting(false);
      return;
    }
    if (checkUserExistenceActionStateResult.status === 'invalid_data') {
      toast({
        type: 'error',
        description: getRegisterCheckErrorMessage('invalid_data')
      });
      setIsSubmitting(false);
      return;
    }
    if (!checkUserExistenceActionStateResult.exists) {
      actionInFlightRef.current = isSocialAuth ? 'social' : 'credentials';
      startTransition(() => {
        // Use social auth action if applicable
        if (isSocialAuth) {
          socialFormAction({
            data: formData_0,
            isWebView
          });
        } else {
          formAction({
            data: formData_0,
            isWebView
          });
        }
      });
      return;
    }
    setIsSubmitting(false);
    registerFormSubmitResultDataError(checkUserExistenceActionStateResult);
  }, [formAction, socialFormAction, isSocialAuth, providerParam, providerUserIdParam, socialRegistrationTokenParam, isWebView, setIsSubmitting, setPhoneNumber, setPendingFormData, verificationTokenId]);
  const registerStatusCallBack = useCallback((status: RegisterStatusCallBackStatusType, resolvedMessage?: string) => {
    const defaultMessages: Record<RegisterStatusCallBackStatusType, string> = {
      failed: 'Falha ao criar conta!',
      invalid_data: 'Falha ao validar seus dados!',
      cloudwalk_unavailable: 'Estamos passando por uma instabilidade no cadastro, tente novamente mais tarde.',
      cloudwalk_validation_error: 'Não foi possível concluir o cadastro. Tente novamente.',
      phone_verification_required: 'Verifique seu telefone novamente para continuar.',
      rate_limited: 'Muitas tentativas de cadastro. Tente novamente mais tarde.'
    };
    const message = status === 'cloudwalk_validation_error' && resolvedMessage ? resolvedMessage : defaultMessages[status];
    toast({
      type: 'error',
      description: message
    });
    analytics?.track?.('Registration Failed', {
      // Descriptive snake_case code for aggregation; human text goes in
      // `error_message`. No `error_code`: the register server action returns
      // semantic statuses, not an HTTP status.
      error: registrationErrorCodeFromStatus(status),
      error_message: message,
      email: registerForm.getValues('email') || undefined,
      timestamp: new Date().toISOString(),
      traits: {
        platform: getNativePlatform(),
        os: getPlatformOS()
      },
      platform: isWebView ? 'app' : 'web'
    });
  }, [analytics, isWebView, registerForm]);
  const redirectAfterLogin = useCallback(() => {
    const homePage = getHomePage(state?.user?.id);
    if (!featureFlags.enableWelcomePage) {
      setIsSubmitting(false);
      router.push(`${homePage}?fromWelcome=true`);
      return;
    }
    if (!state?.openFinanceItem) {
      if (callbackUrl) {
        setIsSubmitting(false);
        router.push(getSafeRedirectUrl(callbackUrl, homePage));
        return;
      }
      const params = new URLSearchParams();
      if (redirect_type) params.set('redirect_type', redirect_type);
      if (subscription_plan) params.set('subscription_plan', subscription_plan);
      setIsSubmitting(false);
      router.push(`/redirect?${params.toString()}`);
      return;
    }
    const welcomePageParams = new URLSearchParams();
    welcomePageParams.set('itemId', state.openFinanceItem.id);
    if (callbackUrl) {
      welcomePageParams.set('callbackUrl', callbackUrl);
      setIsSubmitting(false);
      router.replace(`/welcome/?${welcomePageParams.toString()}`);
      return;
    }
    const redirectPageParams = new URLSearchParams();
    if (redirect_type) redirectPageParams.set('redirect_type', redirect_type);
    if (subscription_plan) redirectPageParams.set('subscription_plan', subscription_plan);
    if (subscription_plan || redirect_type) redirectPageParams.set('return_url', `${window.location.origin}/welcome/?${welcomePageParams.toString()}`);
    redirectPageParams.set('callbackUrl', `${window.location.origin}/welcome/?${welcomePageParams.toString()}`);
    setIsSubmitting(false);
    router.push(`/redirect?${redirectPageParams.toString()}`);
  }, [callbackUrl, redirect_type, router, state, subscription_plan]);
  const registerSuccessStatusCallback = useCallback(async (options?: {
    userId?: string;
    registrationToken?: string;
    cloudwalkUserId?: number;
  }) => {
    toast({
      type: 'success',
      description: 'Conta criada com sucesso!'
    });

    // Use override userId (for social auth) or state userId (for regular auth)
    const userId = options?.userId || state?.user?.id;
    // Use override token (for social auth) or state token (for regular auth)
    const registrationToken = options?.registrationToken || state?.registrationToken;
    const name = registerForm.getValues('name');
    const email = registerForm.getValues('email');
    const phoneNumber = registerForm.getValues('phoneNumber');
    const document = registerForm.getValues('document');
    const dateOfBirth = registerForm.getValues('dateOfBirth');
    if (analytics) {
      if (userId) {
        await analytics.identify(userId, {
          name: name ?? undefined,
          email: email ?? undefined,
          phone: phoneNumber ?? undefined,
          createdAt: new Date().toLocaleString('pt-BR'),
          timestamp: new Date().toISOString(),
          plan: 'basic',
          has_active_subscription: false,
          last_login: new Date().toISOString(),
          ...mergeTrackingParams(firstTouchUTM, ifpCampaignParams),
          traits: {
            platform: getNativePlatform(),
            os: getPlatformOS()
          }
        });
        await analytics.track('Registration Success', {
          email: email ?? undefined,
          timestamp: new Date().toISOString(),
          login_method: 'email',
          platform: isWebView ? 'app' : 'web',
          // CloudWalk cardholder id resolved server-side by the side flow
          // (#3997) and returned on the register action state. Overridden
          // for the social path, which reads socialState instead.
          cloudwalk_user_id: options?.cloudwalkUserId ?? state?.cloudwalkUserId ?? undefined,
          traits: {
            platform: getNativePlatform(),
            os: getPlatformOS()
          }
        });
      }
    }

    // Push GTM dataLayer event so Tag Manager can track registration
    // conversions. Contact data is SHA-256 hashed before the push (see
    // `lib/analytics/gtm-datalayer.ts`), so raw PII never lands in the
    // dataLayer.
    try {
      await pushRegisterSuccessDataLayer({
        userId,
        anonymousId: typeof analytics?.getAnonymousId === 'function' ? analytics.getAnonymousId() : undefined,
        email,
        phone: phoneNumber
      });
    } catch (error) {
      console.error('[Register] GTM dataLayer push failed:', error);
    }
    if (userId) {
      setUserId(userId);
      // Persist the signed registration completion token so the bank-
      // connection step can hand it to `createItemAndGetAuthUrlAction` as a
      // verified userId source on the pre-session WebView <2.0 path (#4126).
      if (registrationToken) {
        setRegistrationToken(registrationToken);
      }

      // Send native auth tokens to mobile app if in WebView using signed registration token
      if (isInMobileWebView() && registrationToken) {
        try {
          await sendNativeAuthToMobile(registrationToken);
        } catch (error_0) {
          console.error('[Register] Failed to send native auth to mobile:', error_0);
          // Don't block registration flow on failure
        }
      }
    }
    setIsSubmitting(false);
    nextStep();
  }, [isWebView, analytics, nextStep, setUserId, setRegistrationToken, state?.user?.id, state?.registrationToken, registerForm, firstTouchUTM, ifpCampaignParams]);

  // Fire the register callback every time a dispatched action COMPLETES — not
  // just when the status changes. A repeated identical failure (e.g.
  // document_exists -> document_exists) wouldn't re-show the toast with a
  // status-only effect because state.status doesn't change between attempts,
  // so we drive the callback off the isPending transition and track which
  // action we dispatched via actionInFlightRef. The pure decision lives in
  // `resolveRegisterCompletion` (unit-tested); this effect only dispatches the
  // resulting side effects.
  useEffect(() => {
    const kind = actionInFlightRef.current;
    const status_0 = kind === 'social' ? socialState?.status : state?.status;
    const completion = resolveRegisterCompletion({
      kind,
      isPending,
      socialIsPending,
      status: status_0
    });
    if (completion.type === 'none') return;
    actionInFlightRef.current = null;
    if (completion.type === 'error') {
      const errorMessage = kind === 'social' ? socialState?.cloudwalkErrorMessage : state?.cloudwalkErrorMessage;
      registerStatusCallBack(completion.status, errorMessage);
      setIsSubmitting(false);
      return;
    }
    if (completion.type === 'release') {
      setIsSubmitting(false);
      return;
    }

    // completion.type === 'success'
    if (kind === 'credentials') {
      registerSuccessStatusCallback();
      return;
    }

    // Social success: web triggers OAuth sign-in; mobile WebView uses token flow.
    if (!isInMobileWebView() && providerParam) {
      registerSuccessStatusCallback({
        userId: socialState?.user?.id,
        registrationToken: socialState?.registrationToken,
        cloudwalkUserId: socialState?.cloudwalkUserId
      }).then(() => {
        // Re-trigger OAuth to create the NextAuth session
        const homePage_0 = getHomePage(socialState?.user?.id);
        nextAuthSignIn(providerParam, {
          callbackUrl: callbackUrl || homePage_0
        });
      });
      return;
    }

    // For mobile WebView, use the existing token-based flow
    registerSuccessStatusCallback({
      userId: socialState?.user?.id,
      registrationToken: socialState?.registrationToken,
      cloudwalkUserId: socialState?.cloudwalkUserId
    });
  }, [isPending, socialIsPending, state?.status, socialState?.status, socialState?.user?.id, socialState?.registrationToken, socialState?.cloudwalkUserId, state?.cloudwalkUserId, registerStatusCallBack, registerSuccessStatusCallback, setUserId, providerParam, callbackUrl]);
  return useMemo(() => ({
    registerForm,
    handleSubmit,
    callbackUrl,
    redirect_type,
    subscription_plan,
    isSendRegisterForm: isSubmitting || isPending || socialIsPending,
    redirectAfterLogin
  }), [registerForm, handleSubmit, callbackUrl, redirect_type, subscription_plan, isSubmitting, state.status, socialState.status, redirectAfterLogin]);
};
export default useRegisterPageFactory;